import { useEffect, useRef, useState } from 'react'

interface Settled {
  /** Index of the row that was acted on */
  index: number
  /** Row count when it settled; focus moves once the refetched list has dropped it */
  countAtSettle: number
}

/**
 * After a request row is approved or declined, move focus to the next row; when none is left, to the first
 * fallback element that exists. Focus must never fall to `<body>` (request-access spec §8.4, §8.9 #6).
 *
 * The move waits for the refetch: the row only leaves the list when the server says so.
 */
export const useRowSettleFocus = ({ count, fallbackIds }: { count: number; fallbackIds: string[] }) => {
  const listRef = useRef<HTMLUListElement | null>(null)
  const [settled, setSettled] = useState<Settled | null>(null)
  const fallbackKey = fallbackIds.join(' ')

  useEffect(() => {
    if (!settled || count >= settled.countAtSettle) {
      return
    }
    const rows = listRef.current?.querySelectorAll('li') ?? []
    const nextRow = count > 0 ? rows[Math.min(settled.index, rows.length - 1)] : undefined
    const fallback = fallbackKey
      .split(' ')
      .filter(Boolean)
      .map(id => document.getElementById(id))
      .find(element => element !== null)
    const target = nextRow?.querySelector<HTMLElement>('button') ?? fallback
    target?.focus()
    setSettled(null)
  }, [count, settled, fallbackKey])

  const settle = (index: number) => setSettled({ index, countAtSettle: count })

  return { listRef, settle }
}
